import { I18n } from '@calendly/i18n'
import { useState } from 'react'

import { useGetUserQuery } from 'common'

import { ExternalAttendeeCard } from './external_attendee_card'
import { ShowMoreButton } from './show_more_button'
import { inviteesCardsStackCss, inviteesStackItemCss } from './styles'

const INVITEES_TO_SHOW = 10

/**
 * Stable when the attendee list is recomposed (e.g. currentUser loads and membership rows are merged or dropped).
 * Do not append list index: indices shift and remount cards, losing CRM resolution and expanded panel state.
 */
const attendeePanelKey = (invitee) => (
  invitee.uuid
  ?? invitee.userUuid
  ?? (invitee.email || null)
  ?? ([invitee.kind, invitee.name, invitee.rsvp].filter(Boolean).join('|') || 'external-attendee')
)

export const ExternalInviteesContent = ({ event, readonly }) => {
  const { data: currentUser } = useGetUserQuery()
  const { invitees, eventMemberships, ownerUserUuid } = event
  let attendees = invitees || []

  if (ownerUserUuid !== currentUser?.uuid) {
    attendees = [...(eventMemberships || []), ...attendees]
  }

  const [listExpanded, setListExpanded] = useState(attendees.length <= INVITEES_TO_SHOW)
  const [openPanelKey, setOpenPanelKey] = useState(null)

  const inviteesToShow = listExpanded ? attendees : attendees.slice(0, INVITEES_TO_SHOW)
  const moreCount = attendees.length - inviteesToShow.length
  const canCollapse = listExpanded && attendees.length > INVITEES_TO_SHOW

  const showMoreButtonText = event.past
    ? String(I18n.t('publisher.meetings.invitees.view_more_attendees', { count: moreCount }))
    : String(I18n.t('publisher.meetings.invitees.view_more_invitees', { count: moreCount }))

  const showFewerButtonText = event.past
    ? String(I18n.t('publisher.meetings.invitees.show_fewer_attendees'))
    : String(I18n.t('publisher.meetings.invitees.show_fewer_invitees'))

  const handleTogglePanel = (key) => {
    setOpenPanelKey((prev) => (prev === key ? null : key))
  }

  const hasMoreToShow = !listExpanded && attendees.length > INVITEES_TO_SHOW

  return (
    <div>
      <div className={inviteesCardsStackCss}>
        {inviteesToShow.map((invitee) => {
          const key = attendeePanelKey(invitee)
          return (
            <div key={key} className={inviteesStackItemCss}>
              <ExternalAttendeeCard
                event={event}
                invitee={invitee}
                readonly={readonly}
                isExpanded={openPanelKey === key}
                onToggle={() => handleTogglePanel(key)}
                panelKey={key}
              />
            </div>
          )
        })}
      </div>
      {hasMoreToShow && (
        <ShowMoreButton event={event} expand={() => setListExpanded(true)}>
          {showMoreButtonText}
        </ShowMoreButton>
      )}
      {canCollapse && (
        <ShowMoreButton event={event} expand={() => setListExpanded(false)}>
          {showFewerButtonText}
        </ShowMoreButton>
      )}
    </div>
  )
}
